// DO NOT EDIT — generated by belch/scripts/gen-wire-protocol.mjs from belch/src/types.ts
import { Hex } from 'viem';
import type { SerializedBattleOffer } from '../types/server-client';

// Offers travel in the signing form — team indices zeroed, open seats at the zero address.
export interface Battle {
  p0: Hex;
  p0TeamIndex: bigint;
  p1: Hex;
  p1TeamIndex: bigint;
  p2: Hex;
  p2TeamIndex: bigint;
  p3: Hex;
  p3TeamIndex: bigint;
  teamRegistry: Hex;
  rngOracle: Hex;
  ruleset: Hex;
  moveManager: Hex;
  matchmaker: Hex;
  engineHooks: Hex[];
}

export interface BattleOffer {
  battle: Battle;
  pairHashNonce: bigint;
  battleMode: number;
}

export interface AddressAuthPayload {
  address: Hex;
  signature: string;
  username: string;
}

export interface ChatMessagePayload {
  message: string;
  battleKey: Hex;
}

export interface SetUsernamePayload {
  username: string;
}

export interface BattleSubPayload {
  battleKey: Hex;
}

export interface BattleChatSubPayload {
  battleKey: Hex;
}

export interface BattleUnsubPayload {
  battleKey: Hex;
}

export interface ChatHistoryRequestPayload {
  battleKey: Hex;
}

export interface SignedBattleInitiatePayload {
  battleKey: Hex;
  p0: Hex;
  p1: Hex;
  signature: Hex;
}

// p0TeamIndex rides as a sibling because the offer's own indices are blinded to 0.
export interface JoinSignedQueuePayload {
  offer: BattleOffer;
  signature: Hex;
  p0TeamIndex: string;
  preferOpponent?: Hex;
}

export interface SignedDirectChallengePayload {
  offer: BattleOffer;
  signature: Hex;
  p0TeamIndex: string;
  p1: Hex;
}

// Canonical seat order [p0, p2, p1, p3] → 0=host, 1=ally, 2=opp1, 3=opp2.
export type PartySeatKind = "host" | "named" | "open" | "cpu";

export interface PartySeatSpec {
  kind: PartySeatKind;
  address?: Hex;
}

export interface PostPartyPayload {
  partyId: string;
  offer: SerializedBattleOffer;
  openSeatsMask: number;
  seats: PartySeatSpec[];
  isCpuInclusive: boolean;
  hostSignature: Hex;
  p0TeamIndex: string;
}

export interface ClaimSeatPayload {
  partyId: string;
  seatIndex: number;
  seatSig?: Hex;
  teamIndex: string;
}

export interface CancelPartyPayload {
  partyId: string;
}

export interface SignedCommitPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  moveHash: Hex;
  signature: Hex;
}

export interface DualSignedRevealPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  committerMoveHash: Hex;
  revealerMoveIndex: number;
  revealerSalt: string; // uint104 as decimal string for JSON
  revealerExtraData: string; // uint16 as decimal string for JSON
  signature: Hex;
}

// Relayed verbatim so the revealer advances its local sim without the chain echo.
export interface CommitterRevealPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  committerMoveIndex: number;
  committerSalt: string; // uint104 as decimal string for JSON
  committerExtraData: string; // uint16 as decimal string for JSON
}

// 2-slot analog of DualSignedRevealPayload — a side's two mons pack into one word.
export interface DualSignedSlotRevealPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  committerMovesHash: Hex;
  revealerSidePacked: string; // packed side word as string for JSON
  signature: Hex;
}

// Slot analog of CommitterRevealPayload.
export interface CommitterSlotRevealPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  committerSidePacked: string; // packed side word as string for JSON
}

// Side-scoped: unicast to `to` only, never the opposing side, so commit-reveal hiding holds.
export interface SlotContribPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  to: Hex;
  seat: number; // the contributor's canonical seat 0-3
  moveIndex: number;
  extraData: number;
}

// The host echoes its resolved 2-slot turn to the ally, who replays it. Side-scoped like SLOT_CONTRIB.
export interface CoopTurnPayload {
  battleKey: Hex;
  turnId: string; // BigInt as string for JSON
  to: Hex;
  seat: number; // the host's canonical seat (0)
  moves: string; // hex of the 25-byte resolved slot-turn stream entry
}

export enum RequestType {
  ADDRESS_AUTH = "AA",
  CHAT_MSG = "CM",
  SET_USERNAME = "SU",
  BATTLE_SUB = "BS",
  BATTLE_CHAT_SUB = "BCS",
  BATTLE_UNSUB = "BU",
  CHAT_HISTORY = "CH",
  SIGNED_BATTLE_INITIATE = "SBI",
  JOIN_SIGNED_QUEUE = "JSQ",
  LEAVE_SIGNED_QUEUE = "LSQ",
  SIGNED_DIRECT_CHALLENGE = "SDC",
  SIGNED_COMMIT = "SC",
  DUAL_SIGNED_REVEAL = "DSR",
  COMMITTER_REVEAL = "CMR",
  DUAL_SIGNED_SLOT_REVEAL = "DSSR",
  COMMITTER_SLOT_REVEAL = "CSR",
  SLOT_CONTRIB = "SLC",
  COOP_TURN = "CPT",
  GET_LEADERBOARD = "GLB",
  GET_DAILY_BOARD = "GDB",
  GET_PROFILE = "GP",
  GET_GAME_STATS = "GGS",
  LINK_TWITTER_BEGIN = "LTB",
  LINK_TWITTER_COMPLETE = "LTC",
  UNLINK_TWITTER = "UT",
  QUERY_QUEUE_STATE = "QQS",
  REPORT_CPU_BATTLE = "RCB",
  POST_PARTY = "POP",
  CLAIM_SEAT = "CLS",
  CANCEL_PARTY = "CXP",
  PONG = "PO",
}

export interface ReportCpuBattlePayload {
  battleKey: Hex;
  difficulty: "easy" | "medium" | "hard" | "extra-hard";
  cpuTeamSlot?: number;
  /** UTC day index when this is a daily-boss attempt. Feed-only, and trusted like the tier —
   *  the daily_runs board stays classified from the chain snapshot. */
  dailyDay?: number;
}

export type LeaderboardMode = "all" | "pvp" | "hardPlusRandom" | "ranked";

export interface GetProfilePayload {
  address: Hex;
}

export interface LinkTwitterCompletePayload {
  // Over `link-twitter:<nonce>`, the nonce from this ws's LINK_TWITTER_BEGIN reply.
  walletSig: Hex;
  oauthCode: string;
  pkceVerifier: string;
}

export type Request =
  | { type: RequestType.ADDRESS_AUTH; payload: AddressAuthPayload }
  | { type: RequestType.CHAT_MSG; payload: ChatMessagePayload }
  | { type: RequestType.SET_USERNAME; payload: SetUsernamePayload }
  | { type: RequestType.BATTLE_SUB; payload: BattleSubPayload }
  | { type: RequestType.BATTLE_CHAT_SUB; payload: BattleChatSubPayload }
  | { type: RequestType.BATTLE_UNSUB; payload: BattleUnsubPayload }
  | { type: RequestType.CHAT_HISTORY; payload: ChatHistoryRequestPayload }
  | { type: RequestType.SIGNED_BATTLE_INITIATE; payload: SignedBattleInitiatePayload }
  | { type: RequestType.JOIN_SIGNED_QUEUE; payload: JoinSignedQueuePayload }
  | { type: RequestType.LEAVE_SIGNED_QUEUE }
  | { type: RequestType.SIGNED_DIRECT_CHALLENGE; payload: SignedDirectChallengePayload }
  | { type: RequestType.POST_PARTY; payload: PostPartyPayload }
  | { type: RequestType.CLAIM_SEAT; payload: ClaimSeatPayload }
  | { type: RequestType.CANCEL_PARTY; payload: CancelPartyPayload }
  | { type: RequestType.SIGNED_COMMIT; payload: SignedCommitPayload }
  | { type: RequestType.DUAL_SIGNED_REVEAL; payload: DualSignedRevealPayload }
  | { type: RequestType.COMMITTER_REVEAL; payload: CommitterRevealPayload }
  | { type: RequestType.DUAL_SIGNED_SLOT_REVEAL; payload: DualSignedSlotRevealPayload }
  | { type: RequestType.COMMITTER_SLOT_REVEAL; payload: CommitterSlotRevealPayload }
  | { type: RequestType.SLOT_CONTRIB; payload: SlotContribPayload }
  | { type: RequestType.COOP_TURN; payload: CoopTurnPayload }
  | { type: RequestType.REPORT_CPU_BATTLE; payload: ReportCpuBattlePayload }
  | { type: RequestType.GET_LEADERBOARD }
  | { type: RequestType.GET_DAILY_BOARD; payload?: GetDailyBoardPayload }
  | { type: RequestType.GET_PROFILE; payload: GetProfilePayload }
  | { type: RequestType.GET_GAME_STATS }
  | { type: RequestType.LINK_TWITTER_BEGIN }
  | { type: RequestType.LINK_TWITTER_COMPLETE; payload: LinkTwitterCompletePayload }
  | { type: RequestType.UNLINK_TWITTER }
  | { type: RequestType.QUERY_QUEUE_STATE }
  | { type: RequestType.PONG };

export enum ResponseType {
  ALL_CLIENTS = "AC",
  CLIENT_DELTA = "CD",
  CHAIN_SUB = "CS",
  CHAT_MSG = "CM",
  USERNAME_SET = "US",
  BATTLE_START = "BS",
  CLIENT_JOINED_BATTLE = "CJB",
  CLIENT_LEFT_BATTLE = "CLB",
  CHAT_HISTORY = "CHH",
  BATTLE_LOG_SNAPSHOT = "BLS",
  BATTLE_LOG_EVENT = "BLE",
  SIGNED_QUEUE_MATCHED = "SQM",
  SIGNED_DIRECT_CHALLENGE_RECEIVED = "SDCR",
  SIGNED_COMMIT_RECEIVED = "SCR",
  DUAL_SIGNED_REVEAL_RECEIVED = "DSRR",
  COMMITTER_REVEAL_RECEIVED = "CMRR",
  DUAL_SIGNED_SLOT_REVEAL_RECEIVED = "DSSRR",
  COMMITTER_SLOT_REVEAL_RECEIVED = "CSRR",
  SLOT_CONTRIB_RECEIVED = "SLCR",
  COOP_TURN_RECEIVED = "CPTR",
  SIMPLE_PM_SHARES_BOUGHT = "SPMSB",
  SIMPLE_PM_SHARES_CLAIMED = "SPMSC",
  QUEUE_JOIN_REJECTED = "QJR",
  LEADERBOARD = "LB",
  DAILY_BOARD = "DB",
  PROFILE = "PR",
  GAME_STATS = "GS",
  LINK_TWITTER_BEGIN_REPLY = "LTBR",
  TWITTER_LINKED = "TL",
  TWITTER_UNLINKED = "TU",
  LINK_TWITTER_REJECTED = "LTR",
  SIGNED_QUEUE_MATCH_ASSIGNED = "SQMA",
  QUEUE_MATCH_ABORTED = "QMA",
  QUEUE_STATE = "QS",
  QUEUE_LISTINGS = "QL",
  PING = "PI",
  STALE_TURN = "ST",
  BATTLE_PRESENCE = "BPR",
  BATTLE_SETTLED = "BSTL",
  PARTY_CARD = "PCD",
  SEAT_CLAIM_RESULT = "SCLR",
  PARTY_UPDATED = "PUP",
  PARTY_READY = "PRDY",
  FRENS_TICKET = "FTK",
}

// The complete refund a fresh game wallet pre-signs before the drip lands — every field of
// the transaction, so the wallet signs what it's handed and holds no policy of its own.
// belch always pins the nonce to 0, which is what lets the signature die on the wallet's
// first real tx instead of hanging over it forever.
export interface DripReturnSpec {
  to: Hex;
  chainId: number;
  nonce: number;
  value: string;
  gas: string;
  maxFeePerGas: string;
  maxPriorityFeePerGas: string;
}

export interface FrensTicketPayload {
  ticket: string;
  ret: DripReturnSpec;
}

export interface StaleTurnPayload {
  battleKey: Hex;
  currentTurnId: number;
}

export interface LeaderboardEntry {
  rank: number;
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  wins: number;
  losses: number;
  pvpWins: number;
  pvpLosses: number;
  easyCpuWins: number;
  easyCpuLosses: number;
  mediumCpuWins: number;
  mediumCpuLosses: number;
  hardCpuWins: number;
  hardCpuLosses: number;
  extraHardCpuWins: number;
  extraHardCpuLosses: number;
  randomWins: number;
  randomLosses: number;
  totalBattles: number;
  lastBattleAt: number | null;
  // Null until the daily batch produces a rating for this player.
  rating: number | null;
  rd: number | null;
  ratingDelta: number | null;
  periodBattles: number;
}

export interface LeaderboardModePayload {
  entries: LeaderboardEntry[];
  // Set only when the requester has stats but isn't in this mode's top N.
  self: LeaderboardEntry | null;
}

/** Requesting a day other than today is allowed — the board is derived per UTC day index. */
export interface GetDailyBoardPayload {
  day?: number;
  /** The client's own derivation hash; belch logs a mismatch rather than rejecting. */
  encounterHash?: string;
}

export interface DailyBoardEntry {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  /** Set only for cleared runs; non-clearers are listed unranked. */
  rank: number | null;
  cleared: boolean;
  /** Turns of the player's best cleared run; null when they haven't cleared. */
  turns: number | null;
  /** Survivors of that run; null when they haven't cleared, or it couldn't be decoded. */
  survivors: number | null;
  attempts: number;
}

export interface DailyBoardPayload {
  day: number;
  /** The client cross-checks this against its own derivation; a mismatch means drift. */
  encounterHash: string;
  field: "flux" | "elemental" | "unstable";
  battleMode: 0 | 1;
  monIndices: number[];
  cleared: number;
  attempts: number;
  entries: DailyBoardEntry[];
  /** The requester's own row when they're outside the returned page. */
  self: DailyBoardEntry | null;
}

export interface LeaderboardResponsePayload {
  all: LeaderboardModePayload;
  pvp: LeaderboardModePayload;
  hardPlusRandom: LeaderboardModePayload;
  ranked: LeaderboardModePayload;
}

export interface OpponentMatchEntry {
  battleKey: Hex;
  result: "win" | "loss";
  completedAt: number;
  cpuDifficulty: "easy" | "medium" | "hard" | "extra-hard" | null; // null for pvp
  txHash: Hex | null;
}

export interface OpponentEntry {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  isCpu: boolean;
  myWins: number;
  myLosses: number;
  lastBattleAt: number;
  // Top-5, freshest first — bundled so drawer expansion is zero-round-trip.
  recentMatches: OpponentMatchEntry[];
}

export interface RecentMatchEntry {
  battleKey: Hex;
  opponent: Hex;
  opponentUsername: string | null;
  opponentTwitterHandle: string | null;
  isCpu: boolean;
  result: "win" | "loss";
  completedAt: number;
  cpuDifficulty: "easy" | "medium" | "hard" | "extra-hard" | null; // null for pvp
  txHash: Hex | null;
}

// byQuestId key -1 covers rows written before quest_id was tracked.
export interface QuestCompletionsSummary {
  total: number;
  byQuestId: Record<number, number>;
}

// delta is null without a prior_rating; pvpBattlesLifetime mirrors the visibility gate (>= 5).
export interface RatingSummary {
  rating: number;
  rd: number;
  delta: number | null;
  pvpBattlesLifetime: number;
}

export interface ProfileResponsePayload {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  stats: StatsSummary | null;
  lastBattleAt: number | null;
  // Both modes' ranks for the leaderboard rank badge. null = no stats row.
  leaderboardRank: { all: number | null; pvp: number | null };
  rating: RatingSummary | null;
  opponents: OpponentEntry[];
  recentMatches: RecentMatchEntry[];
  quests: QuestCompletionsSummary;
}

// Global stats payload — feeds the /stats page. Memoized server-side
// for STATS_TTL (5 min). `generatedAt` lets the UI render staleness.
export interface GameStatsMonCount { monId: number; count: number; }

export interface GameStatsMonUsage { monId: number; appearances: number; wins: number; }

export interface GameStatsIntBucket { bucket: number; count: number; }

export interface GameStatsQuestDay { day: string; completions: number; players: number; }

export interface GameStatsTopRated {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  rating: number;
  ratingDelta: number | null;
}

export interface GameStatsPayload {
  generatedAt: number;
  starters: GameStatsMonCount[];
  monUsage: GameStatsMonUsage[];
  turnLengthHistogram: GameStatsIntBucket[];
  battleVolume: {
    total: number;
    pvp: number;
    easyCpu: number;
    mediumCpu: number;
    hardCpu: number;
    phantom: number;
    last24h: number;
    last7d: number;
  };
  hardCpuDefeatRate: number;
  phantomTeamShareOfCpu: number;
  quest: {
    perDay: GameStatsQuestDay[];
    completionPctLast24h: number;
  };
  topRated: GameStatsTopRated[];
}

export interface LinkTwitterBeginReplyPayload {
  // The nonce itself is returned so the client can embed it in the signed
  // message and in the OAuth state param if desired.
  nonce: string;
  expiresAt: number;
  // Exact string the wallet should sign — spells out the format so the
  // client doesn't have to hardcode the prefix.
  message: string;
}

export interface TwitterLinkedPayload {
  address: Hex;
  twitterId: string;
  twitterHandle: string;
}

export interface TwitterUnlinkedPayload {
  address: Hex;
}

export type TwitterLinkRejectReason =
  | "NOT_CONFIGURED"
  | "RATE_LIMITED"
  | "INVALID_NONCE"
  | "NONCE_EXPIRED"
  | "INVALID_SIGNATURE"
  | "OAUTH_EXCHANGE_FAILED"
  | "TWITTER_FETCH_FAILED";

export interface LinkTwitterRejectedPayload {
  reason: TwitterLinkRejectReason;
}

export type QueueJoinRejectReason =
  | "ALREADY_IN_QUEUE"
  | "MATCH_IN_FLIGHT"
  | "INVALID_SIGNATURE";

export type QueueJoinRejectedPayload = {
  reason: QueueJoinRejectReason;
};

export type AllClientsPayload = {
  clients: {
    address: Hex;
    username: string;
    stats: StatsSummary | null;
    twitterHandle: string | null;
  }[];
};

export type ClientDeltaPayload = {
  added: {
    address: Hex;
    username: string;
    stats: StatsSummary | null;
    twitterHandle: string | null;
  }[];
  removed: { address: Hex }[];
};

export type ChainSubscriptionResponsePayload = {
  data: Hex;
  topics: [] | [signature: Hex, ...args: Hex[]];
};

/** Inline stats carried with identity in hot-path broadcasts (BATTLE_START,
 *  BattleLogEvent). Null = no player_stats row yet for (address, network).
 *  Counters:
 *    wins / losses              — every battle, both sides included
 *    pvpWins / pvpLosses        — battles where neither side was a CPU
 *    easyCpuWins / easyCpuLosses     — vs OKAY_CPU
 *    mediumCpuWins / mediumCpuLosses — vs FAIR_CPU
 *    hardCpuWins / hardCpuLosses     — vs BETTER_CPU
 *    randomWins / randomLosses  — battles where p1TeamIndex matches
 *                                 uint16(uint160(p0)) (phantom team).
 *                                 Orthogonal to CPU tier — a phantom
 *                                 hard-CPU win bumps both counters. */
export interface StatsSummary {
  wins: number;
  losses: number;
  pvpWins: number;
  pvpLosses: number;
  easyCpuWins: number;
  easyCpuLosses: number;
  mediumCpuWins: number;
  mediumCpuLosses: number;
  hardCpuWins: number;
  hardCpuLosses: number;
  extraHardCpuWins: number;
  extraHardCpuLosses: number;
  randomWins: number;
  randomLosses: number;
  totalBattles: number;
}

/** BATTLE_START lobby notification — carries inline identity + stats so the
 *  match screen can render "vs @alice 𝕏 (12W-3L)" with zero round-trips. */
export interface BattleStartResponsePayload {
  battleKey: Hex;
  p0: Hex;
  p0Username: string | null;
  p0Stats: StatsSummary | null;
  p0TwitterHandle: string | null;
  p1: Hex;
  p1Username: string | null;
  p1Stats: StatsSummary | null;
  p1TwitterHandle: string | null;
  // Party seats (Multi): the ally (p2) and second opponent (p3) so seat-claimers who aren't in
  // p0/p1 can recognize themselves and navigate in. ZERO_ADDRESS / absent for non-party battles.
  p2?: Hex;
  p3?: Hex;
}

export interface ChatMessageResponsePayload {
  id: string;
  address: Hex;
  battleKey: Hex;
  message: string;
  username: string;
  twitterHandle: string | null;
  timestamp: number;
}

export interface ChatHistoryMessage {
  id: string;
  timestamp: number;
  address: Hex;
  username: string;
  twitterHandle: string | null;
  message: string;
}

export interface ChatHistoryPayload {
  battleKey: Hex;
  messages: ChatHistoryMessage[];
}

export interface UsernameSetPayload {
  address: Hex;
  username: string;
}

export interface ClientBattlePayload {
  address: Hex;
  username: string | null;
  twitterHandle: string | null;
  battleKey: Hex;
}

// Battle-scoped participant liveness, derived from the PONG heartbeat.
export interface BattlePresencePayload {
  battleKey: Hex;
  address: Hex;
  status: "online" | "offline";
}

/** Battle-log feed events — each carries resolved usernames inline. */
export type BattleLogEvent =
  | {
      id: number;
      timestamp: number;
      type: "BATTLE_START";
      battleKey: Hex;
      p0: Hex;
      p0Username: string | null;
      p0Stats: StatsSummary | null;
      p0TwitterHandle: string | null;
      p1: Hex;
      p1Username: string | null;
      p1Stats: StatsSummary | null;
      p1TwitterHandle: string | null;
      // Party seats so p2/p3 claimers can self-recognize (ZERO / absent for non-party battles).
      p2?: Hex;
      p3?: Hex;
      // Client-reported CPU difficulty (collapsed CPU: not derivable from p1). Best-effort on
      // BATTLE_START (the report may race the chain event); reliable by BATTLE_COMPLETE.
      cpuDifficulty?: "easy" | "medium" | "hard" | "extra-hard";
      mode: "singles" | "doubles" | "multi";
      // Client-reported daily-boss attempt; the client derives the encounter from the day.
      dailyDay?: number;
    }
  | {
      id: number;
      timestamp: number;
      type: "BATTLE_COMPLETE";
      battleKey: Hex;
      winner: Hex;
      winnerUsername: string | null;
      winnerTwitterHandle: string | null;
      loser: Hex;
      loserUsername: string | null;
      loserTwitterHandle: string | null;
      cpuDifficulty?: "easy" | "medium" | "hard" | "extra-hard";
      mode: "singles" | "doubles" | "multi";
      dailyDay?: number;
    }
  | {
      id: number;
      timestamp: number;
      type: "GACHA_ROLL";
      player: Hex;
      playerUsername: string | null;
      playerTwitterHandle: string | null;
      monIds: number[];
    }
  | {
      id: number;
      timestamp: number;
      type: "GACHA_FULL_ROLL";
      player: Hex;
      playerUsername: string | null;
      playerTwitterHandle: string | null;
      monIds: number[];
    }
  | {
      id: number;
      timestamp: number;
      type: "MON_LEVEL_UP";
      battleKey: Hex;
      player: Hex;
      playerUsername: string | null;
      playerTwitterHandle: string | null;
      monId: number;
      oldLevel: number;
      newLevel: number;
    };

export interface BattleLogSnapshotPayload {
  events: BattleLogEvent[];
}

export interface BattleLogEventPayload {
  event: BattleLogEvent;
}

export interface SignedQueueMatchedPayload {
  offer: SerializedBattleOffer;
  signature: Hex;
  p0TeamIndex: string;
  p0Username: string;
  p0TwitterHandle: string | null;
}

export interface SignedDirectChallengeReceivedPayload {
  offer: SerializedBattleOffer;
  signature: Hex;
  p0TeamIndex: string;
  p0Username: string;
  p0TwitterHandle: string | null;
}

// P0 doesn't submit on-chain — they wait for BATTLE_START, or the deadline aborts the match.
export interface SignedQueueMatchAssignedPayload {
  opponentAddress: Hex;
  opponentUsername: string;
  opponentTwitterHandle: string | null;
  deadline: number;
}

export type QueueMatchAbortReason =
  | "OPPONENT_DISCONNECTED"
  | "OPPONENT_TX_TIMEOUT"
  | "INTERNAL";

// Torn down before BATTLE_START; `reQueued` means P0 was re-enqueued with their offer.
export interface QueueMatchAbortedPayload {
  reason: QueueMatchAbortReason;
  reQueued: boolean;
}

interface QueueOpponentView {
  address: Hex;
  username: string;
  twitterHandle: string | null;
}

// Also sent unsolicited on ADDRESS_AUTH so reconnects re-sync.
export type QueueParticipantState =
  | { kind: "idle" }
  | { kind: "waiting"; joinedAt: number; queuedSize: number }
  | { kind: "matched_p0"; opponent: QueueOpponentView; matchedAt: number; deadline: number }
  | { kind: "matched_p1"; opponent: QueueOpponentView; matchedAt: number; deadline: number }
  // P0 issued a direct challenge and is awaiting the target's accept + BattleStart.
  | { kind: "challenging"; opponent: QueueOpponentView; deadline: number };

export interface QueueStatePayload {
  state: QueueParticipantState;
}

export interface QueueListing {
  address: Hex;
  username: string;
  twitterHandle: string | null;
  battleMode: number;
  joinedAt: number;
}

// Replaces the client's list wholesale; `total` is the untruncated count.
export interface QueueListingsPayload {
  listings: QueueListing[];
  total: number;
}

// No bigint in wire payload types — JSON.stringify throws on them; chain values go as strings.
// The deposit amount is not on the wire — it equals the delta of totalDeposits between events.
export interface SimplePMSharesBoughtPayload {
  battleKey: Hex;
  buyer: Hex;
  // Bettors trade from a market wallet that is usually not a connected client, so the
  // name resolves here (presence, then DB) rather than off the client's online roster.
  buyerUsername: string | null;
  buyerTwitterHandle: string | null;
  isP0: boolean;
  sharesMinted: string; // post-discount shares credited
  p0Shares: string; // market totals AFTER this trade
  p1Shares: string;
  totalDeposits: string;
}

export interface SimplePMSharesClaimedPayload {
  battleKey: Hex;
  claimant: Hex;
  amount: string; // wei claimed, as decimal string
}

// Authoritative onchain settlement, pushed to battle subscribers (the lobby
// BATTLE_COMPLETE feed excludes them).
export interface BattleSettledPayload {
  battleKey: Hex;
  winner: Hex;
}

// Redacted per-seat view for cards + updates (never carries the seat sig).
export interface PartySeatView {
  kind: PartySeatKind;
  address?: Hex;
  username?: string;
  filled: boolean;
}

// yourSeatIndex is the recipient's seat, or null for a lobby-wide advert where they
// may claim any open one.
export interface PartyCardPayload {
  partyId: string;
  host: Hex;
  hostUsername?: string;
  offer: SerializedBattleOffer;
  openSeatsMask: number;
  seats: PartySeatView[];
  isCpuInclusive: boolean;
  yourSeatIndex: number | null;
}

export interface SeatClaimResultPayload {
  partyId: string;
  seatIndex: number;
  accepted: boolean;
  reason?: string;
}

// A seats-changed pulse to a party's members. An empty seats array is the
// cancel/expiry sentinel — the party the recipient was in no longer exists.
export interface PartyUpdatedPayload {
  partyId: string;
  seats: PartySeatView[];
}

// The host assembles startGame from this — belch never signs or writes. Host and cpu
// seats carry a null sig.
export interface PartyReadyPayload {
  partyId: string;
  addresses: (Hex | null)[];
  teamIndices: string[];
  seatSigs: (Hex | null)[];
  isCpuInclusive: boolean;
}

export type MyResponse =
  | { type: ResponseType.ALL_CLIENTS; payload: AllClientsPayload }
  | { type: ResponseType.CLIENT_DELTA; payload: ClientDeltaPayload }
  | { type: ResponseType.CHAT_MSG; payload: ChatMessageResponsePayload }
  | { type: ResponseType.USERNAME_SET; payload: UsernameSetPayload }
  | { type: ResponseType.CHAIN_SUB; payload: ChainSubscriptionResponsePayload }
  | { type: ResponseType.BATTLE_START; payload: BattleStartResponsePayload }
  | { type: ResponseType.CLIENT_JOINED_BATTLE; payload: ClientBattlePayload }
  | { type: ResponseType.CLIENT_LEFT_BATTLE; payload: ClientBattlePayload }
  | { type: ResponseType.CHAT_HISTORY; payload: ChatHistoryPayload }
  | { type: ResponseType.BATTLE_LOG_SNAPSHOT; payload: BattleLogSnapshotPayload }
  | { type: ResponseType.BATTLE_LOG_EVENT; payload: BattleLogEventPayload }
  | { type: ResponseType.SIGNED_QUEUE_MATCHED; payload: SignedQueueMatchedPayload }
  | { type: ResponseType.SIGNED_DIRECT_CHALLENGE_RECEIVED; payload: SignedDirectChallengeReceivedPayload }
  | { type: ResponseType.SIGNED_COMMIT_RECEIVED; payload: SignedCommitPayload }
  | { type: ResponseType.DUAL_SIGNED_REVEAL_RECEIVED; payload: DualSignedRevealPayload }
  | { type: ResponseType.COMMITTER_REVEAL_RECEIVED; payload: CommitterRevealPayload }
  | { type: ResponseType.DUAL_SIGNED_SLOT_REVEAL_RECEIVED; payload: DualSignedSlotRevealPayload }
  | { type: ResponseType.COMMITTER_SLOT_REVEAL_RECEIVED; payload: CommitterSlotRevealPayload }
  | { type: ResponseType.SLOT_CONTRIB_RECEIVED; payload: SlotContribPayload }
  | { type: ResponseType.COOP_TURN_RECEIVED; payload: CoopTurnPayload }
  | { type: ResponseType.SIMPLE_PM_SHARES_BOUGHT; payload: SimplePMSharesBoughtPayload }
  | { type: ResponseType.SIMPLE_PM_SHARES_CLAIMED; payload: SimplePMSharesClaimedPayload }
  | { type: ResponseType.QUEUE_JOIN_REJECTED; payload: QueueJoinRejectedPayload }
  | { type: ResponseType.LEADERBOARD; payload: LeaderboardResponsePayload }
  | { type: ResponseType.DAILY_BOARD; payload: DailyBoardPayload }
  | { type: ResponseType.PROFILE; payload: ProfileResponsePayload }
  | { type: ResponseType.GAME_STATS; payload: GameStatsPayload }
  | { type: ResponseType.LINK_TWITTER_BEGIN_REPLY; payload: LinkTwitterBeginReplyPayload }
  | { type: ResponseType.TWITTER_LINKED; payload: TwitterLinkedPayload }
  | { type: ResponseType.TWITTER_UNLINKED; payload: TwitterUnlinkedPayload }
  | { type: ResponseType.LINK_TWITTER_REJECTED; payload: LinkTwitterRejectedPayload }
  | { type: ResponseType.SIGNED_QUEUE_MATCH_ASSIGNED; payload: SignedQueueMatchAssignedPayload }
  | { type: ResponseType.QUEUE_MATCH_ABORTED; payload: QueueMatchAbortedPayload }
  | { type: ResponseType.QUEUE_STATE; payload: QueueStatePayload }
  | { type: ResponseType.QUEUE_LISTINGS; payload: QueueListingsPayload }
  | { type: ResponseType.PING }
  | { type: ResponseType.STALE_TURN; payload: StaleTurnPayload }
  | { type: ResponseType.BATTLE_PRESENCE; payload: BattlePresencePayload }
  | { type: ResponseType.BATTLE_SETTLED; payload: BattleSettledPayload }
  | { type: ResponseType.PARTY_CARD; payload: PartyCardPayload }
  | { type: ResponseType.SEAT_CLAIM_RESULT; payload: SeatClaimResultPayload }
  | { type: ResponseType.PARTY_UPDATED; payload: PartyUpdatedPayload }
  | { type: ResponseType.PARTY_READY; payload: PartyReadyPayload }
  | { type: ResponseType.FRENS_TICKET; payload: FrensTicketPayload };
